import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/** Footer-only discovery. No cart requests, tracking or audio loading before a click. */
class MoxDuelBonus extends Component {
  requiredRefs = ['dialog', 'reward', 'motion', 'code', 'status', 'audioControls', 'sound', 'audioStatus'];
  #events;
  #audio;
  #returnFocus;
  #boundDialog;
  #copyAttempt = 0;
  #audioAttempt = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#bindDialog();
  }

  updatedCallback() {
    this.#events?.abort();
    this.#stop();
    super.updatedCallback();
    this.#bindDialog();
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#stop();
    super.disconnectedCallback();
  }

  #bindDialog() {
    this.#events?.abort();
    this.#events = new AbortController();
    this.#boundDialog = this.refs.dialog;
    const options = { signal: this.#events.signal };
    document.addEventListener('mox:audio-play', (event) => {
      if (event.detail?.owner === this || !this.#audio) return;
      this.#audioAttempt++;
      this.#audio.pause();
      this.refs.sound?.setAttribute('aria-pressed', 'false');
    }, options);
    this.refs.dialog.addEventListener('close', () => {
      this.#stop();
      if (this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
    }, options);
    this.refs.dialog.addEventListener('click', (event) => {
      if (event.target !== this.refs.dialog) return;
      const rect = this.refs.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDuel();
    }, options);
  }

  /** @param {MouseEvent} event Native button handles Enter and Space. */
  openDuel(event) {
    const origin = event.composedPath?.()[0] ?? event.target;
    if (!(origin instanceof Element) || !origin.closest('.mox-footer-duel__trigger')) return;
    // Mouse and touch can only activate the actual card rectangles, never the full scene.
    if (event.detail > 0 && !origin.closest('[data-duel-hit]')) return;
    const { dialog, reward, motion, status } = this.refs;
    if (dialog.open) return;
    this.#returnFocus = this.querySelector('.mox-footer-duel__trigger:not([aria-hidden])');
    reward.hidden = false;
    motion.hidden = false;
    motion.textContent = motion.dataset.pause;
    this.classList.remove('is-scene-paused');
    status.textContent = '';
    this.refs.audioStatus.textContent = '';
    this.classList.add('is-open');
    lockScroll(dialog);
    dialog.showModal();
    this.#startAudio();
  }

  toggleMotion() {
    if (!this.refs.dialog.open || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const paused = this.classList.toggle('is-scene-paused');
    this.refs.motion.textContent = paused ? this.refs.motion.dataset.resume : this.refs.motion.dataset.pause;
  }

  closeDuel() {
    this.refs.dialog.close();
  }

  #stop() {
    this.#copyAttempt++;
    this.#audioAttempt++;
    this.classList.remove('is-open', 'is-scene-paused');
    if (this.#audio) {
      this.#audio.pause();
      this.#audio.currentTime = 0;
    }
    this.refs.sound?.setAttribute('aria-pressed', 'false');
    if (this.refs.status) this.refs.status.textContent = '';
    if (this.refs.audioStatus) this.refs.audioStatus.textContent = '';
    if (this.#boundDialog) {
      if (this.#boundDialog.open) this.#boundDialog.close();
      unlockScroll(this.#boundDialog);
    }
  }

  async copyCode() {
    const attempt = ++this.#copyAttempt;
    const dialog = this.refs.dialog;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(this.refs.code.textContent.trim());
      if (attempt !== this.#copyAttempt || !dialog.open || dialog !== this.refs.dialog) return;
      this.refs.status.textContent = this.refs.status.dataset.copied;
    } catch {
      if (attempt !== this.#copyAttempt || !dialog.open || dialog !== this.refs.dialog) return;
      try {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(this.refs.code);
        selection?.removeAllRanges();
        selection?.addRange(range);
      } catch {
        // The manual-copy instruction remains available if selection is blocked too.
      }
      this.refs.status.textContent = this.refs.status.dataset.copyFailed;
    }
  }

  #startAudio() {
    const src = this.dataset.audioSrc?.trim();
    if (!src) return;
    if (!this.#audio) {
      this.#audio = new Audio(src);
      this.#audio.preload = 'none';
      this.#audio.volume = 0.3;
      this.#audio.addEventListener('ended', () => {
        if (this.#audio.ended && this.refs.dialog.open) this.refs.sound.setAttribute('aria-pressed', 'false');
      });
    }
    this.refs.audioControls.hidden = false;
    this.#playAudio();
  }

  #playAudio() {
    document.dispatchEvent(new CustomEvent('mox:audio-play', { detail: { owner: this } }));
    const attempt = ++this.#audioAttempt;
    const dialog = this.refs.dialog;
    this.#audio.play().then(() => {
      if (attempt !== this.#audioAttempt || dialog !== this.refs.dialog) return;
      if (!dialog.open) { this.#audio.pause(); return; }
      this.refs.sound.setAttribute('aria-pressed', 'true');
      this.refs.audioStatus.textContent = '';
    }).catch(() => {
      if (attempt !== this.#audioAttempt || !dialog.open || dialog !== this.refs.dialog) return;
      this.refs.sound.setAttribute('aria-pressed', 'false');
      this.refs.audioStatus.textContent = this.refs.audioStatus.dataset.audioUnavailable;
    });
  }

  toggleSound() {
    if (!this.#audio) return;
    if (this.#audio.paused) this.#playAudio();
    else { this.#audioAttempt++; this.#audio.pause(); this.refs.sound.setAttribute('aria-pressed', 'false'); }
  }

  /** @param {Event} event */
  changeVolume(event) {
    if (this.#audio && event.target instanceof HTMLInputElement) this.#audio.volume = Number(event.target.value);
  }
}

if (!customElements.get('mox-duel-bonus')) customElements.define('mox-duel-bonus', MoxDuelBonus);

